import React from 'react'
import { Anchor, Text } from '@mantine/core'
import { LinkAnchor } from '@phoenix-web/react-shared/client-components'
import { formatPhone } from '@phoenix-web/utils/general'
import {
  CONTACT_US_URL,
  COOKIE_POLICY_URL,
  DO_NOT_SELL_INFO_URL,
  PRIVACY_POLICY_URL,
  TERMS_OF_USE_URL,
} from '@/constants'

/** GLOBAL TEXT */
export const LOAN_HUB = 'Loan Hub'

/** GLOBAL TEXT END */

/** META TAG RELATED STRINGS - START */
export const SITE_META_TITLE = 'Knock Dashboard'
export const SITE_META_ROOT_TITLE = 'Knock Dashboard | Manage the Bridge Loan from Start to Finish'
export const SITE_META_DESCRIPTION =
  'The Knock dashboard is the homebase for borrowers to manage their bridge loan journey—view loan status details, complete tasks, upload documents, and connect with their Loan Officer.'
export const generateMetaTitle = (title: string) => `${title} - ${SITE_META_TITLE}`
/** META TAG RELATED STRINGS - END */

/** KNOCK SUPPORT STRINGS - START **/
export const SUPPORT_PHONE = '8669961695'
export const LISTING_TEAM_SUPPORT_PHONE = '4702508421'
export const SUPPORT_HOURS = 'Mon-Fri 9am ET to 6pm ET'
export const SUPPORT_EMAIL = 'hello@knock.com'
export const AUTH_SUPPORT_EMAIL = 'support@knock.com'
export const CALENDLY_SUPPORT_LINK_URL =
  'https://calendly.com/d/5cs-gtr-pcj?utm_source=customer_dashboard&utm_medium=website&utm_campaign=customer_dashboard_revamp'

export const CALL_LOAN_OFFICER_PROMPT = 'Already working with a loan officer? Be sure to contact them first.'
export const GENERAL_SUPPORT_TITLE = 'For general support questions'
export const GENERAL_SUPPORT_SUBTITLE = 'Contact our Knock Customer Support team by phone or email.'
export const GET_SUPPORT_MODAL_TITLE = 'How can we help you?'
export const LOAN_SUPPORT_EMAIL = 'loansupport@knock.com'
export const LISTING_TEAM_SUPPORT_EMAIL = 'listings@knock.com'
export const SCHEDULE_A_CALL_LINK_TEXT = 'Schedule a call'
/** KNOCK SUPPORT STRINGS - END **/

/** FOOTER STRINGS - START */
export const FOOTER_LINKS = [
  {
    link: CONTACT_US_URL,
    label: 'Contact Us',
    target: '_blank',
  },
  {
    link: TERMS_OF_USE_URL,
    label: 'Terms of Use',
    target: '_blank',
  },
  {
    link: PRIVACY_POLICY_URL,
    label: 'Privacy Policy',
    target: '_blank',
  },
  {
    link: COOKIE_POLICY_URL,
    label: 'Cookie Policy',
    target: '_blank',
  },
  {
    link: DO_NOT_SELL_INFO_URL,
    label: 'Do Not Sell My Information',
    target: '_blank',
  },
] as const

export const KNOCK_LENDING_NMLS_ID = '1958445'
export const KNOCK_LENDING_NMLS = `Knock Lending LLC, NMLS #${KNOCK_LENDING_NMLS_ID}`
export const COPYRIGHT = `© ${new Date().getFullYear()} Knockaway, Inc. All rights reserved.`
export const FOOTER_EQUAL_HOUSING = 'Equal Housing Opportunity'
export const FOOTER_COMPANY = (
  <>
    Knock Lending LLC
    <br />
    NMLS #{KNOCK_LENDING_NMLS_ID}
    <br />
    3715 Northside Pkwy, Building 100, Suite 500, Atlanta, GA 30327
    <br />
    <Anchor href={`tel:${SUPPORT_PHONE}`}>{formatPhone(SUPPORT_PHONE)}</Anchor>
  </>
)
export const FOOTER_LEGAL_STATEMENT_ARR = [
  'Please be advised that Knock Lending, LLC and Knock Property 1, LLC are wholly owned subsidiaries of Knockaway, Inc. (collectively "Knock") and you are NOT required to transact with any of these entities as a condition of working with Knock.',
  'Knock Property 1, LLC issues a Knock Purchase Offer ("KPO") on qualifying properties and charges a contract fee in connection with the KPO.',
  'Equal housing lender. Make sure you understand the features associated with the loan program you choose, and that it meets your unique financial needs. This is not a credit decision or a commitment to lend. Eligibility is subject to completion of an application and verification of home ownership, occupancy, title, income, employment, credit, home value, collateral, and other underwriting requirements as determined by Knock Lending, LLC.',
]
/** FOOTER STRINGS - END */

/** STAGING PAGES TEXT - START */
export const LOG_IN = 'Log in'
export const CREATE_ACCOUNT = 'Create your account'
export const ALREADY_HAVE_ACCOUNT = 'Already have an account?'
export const DONT_HAVE_ACCOUNT = 'Don’t have an account?'
export const NEW_TO_KNOCK = 'New to Knock?'

export const createAccountContent = {
  WELCOME_TITLE: 'Welcome to your Knock dashboard.',
  WELCOME_SUBTITLE: 'Create your account to get started!',
  LIST_TITLE: 'Your Knock dashboard is your homebase to:',
  LIST_ITEMS: [
    'Track your bridge loan journey from start to finish.',
    'Stay organized with to-do lists and document uploads.',
    'Connect with your Loan Officer and the Knock team.',
  ],
  AND_SO_MUCH_MORE: 'And so much more!',
}

export const loginContent = {
  TITLE: 'Welcome back to Knock.',
  SUBTITLE: 'Let’s pick up where you left off.',
  TEXT: 'Log in to view your loan status, upload documents, and stay on track.',
}
/** STAGING PAGES TEXT - END*/

/** LOAN HUB ACCESS MODAL TEXT */
export const loanHubAccessModal = {
  TITLE: 'Please verify your email',
  MESSAGE: (
    <>
      Check your email to verify your account. Once verified, you’ll have full access to the {LOAN_HUB}.<br />
      <br />
      Unable to locate your account verification email? Be sure to check your spam, junk and promotions folders by
      searching {AUTH_SUPPORT_EMAIL}. If you are still unable to locate the email please reach out to the{' '}
      <LinkAnchor href={CONTACT_US_URL}>Knock Support team</LinkAnchor>.
    </>
  ),
  BUTTON_TEXT: 'I have verified my email',
}
/** LOAN HUB ACCESS MODAL TEXT END */

/** BLEND STAGING PAGES TEXT */
export const blendPagesContent = {
  BLEND_WELCOME: 'Let’s get your bridge loan moving.',
  BLEND_WELCOME_SUBTITLE: 'Start your loan application in minutes.',
  BLEND_WELCOME_BACK: 'Welcome back',
  BLEND_LIST_TITLE: 'Make your loan journey stress-free:',
  BLEND_LIST_ITEMS: [
    'Stay on top of every step in the process.',
    'Easily upload documents and track your progress.',
    'Get answers fast from your Loan Officer and team.',
  ],
  BLEND_VIEW_LOAN_HUB_TEXT: 'View my Loan Hub',
  BLEND_WELCOME_BACK_SUBTITLE: (
    <>
      Click below to access your Knock Loan Hub.
      <br />
      <br />
      If you&apos;re experiencing issues accessing your loan hub. We&apos;re just an email away, contact Knock Support
      at{' '}
      <Anchor fw={700} className="simple-link-blue" href={`mailto:${SUPPORT_EMAIL}`}>
        {SUPPORT_EMAIL}
      </Anchor>
      .
    </>
  ),
  login: {
    ctaButtonText: LOG_IN,
    altCtaText: NEW_TO_KNOCK,
    altCtaLinkLabel: CREATE_ACCOUNT,
  },
  signup: {
    ctaButtonText: CREATE_ACCOUNT,
    altCtaText: ALREADY_HAVE_ACCOUNT,
    altCtaLinkLabel: LOG_IN,
  },
}

/** MERIDIANLINK LOANHUB SSO OPENING TAB TEXT */
export const loanHubOpeningContent = {
  OPENING_TITLE: 'Taking you to your Loan Hub',
  OPENING_MESSAGE: 'Just a moment...',
}

/** MERIDIANLINK LOANHUB PAGE TEXT */
export const loanHubPagesContent = {
  LOANHUB_WELCOME: 'Let\u2019s get your loan moving.',
  LOANHUB_WELCOME_SUBTITLE: 'Continue your application with Knock.',
  LOANHUB_LOGOUT_PROMPT: 'Not you?',
  LOANHUB_LOGOUT_LINK_TEXT: 'Log out',
}

/** AUTH PAGES TEXT */
export const authChooseContent = {
  TITLE: 'Welcome back.',
  SUBTITLE: 'Log in to your account',
  LOGIN_TEXT: 'Log in',
  SIGNUP_TEXT: 'Create account',
}
export const authSignupContent = {
  TITLE: 'Create account',
  WELCOME_TITLE: 'Welcome.',
  TITLE_AUDIENCE_PICKER: 'Which describes you best?',
  LOGIN_TEXT: ALREADY_HAVE_ACCOUNT,
  LOGIN_LINK_LABEL: LOG_IN,
  ALT_CREATE_ACCOUNT: 'Create an account',
}
export const authVerifyContent = {
  TITLE: 'Check your inbox',
  CHECK_EMAIL_TEXT: 'To complete your account, click the verification link in the email we’ve sent to you.',
  RESEND_EMAIL_TEXT: 'Resend verification email',
  ALREADY_VERIFIED_TEXT: 'Already verified?',
  LOGIN_LINK_LABEL: LOG_IN,
  GO_TO_DASHBOARD_LINK_LABEL: 'Go to dashboard',

  VERIFIED_TITLE_SUCCESS: 'Email verified',
  VERIFIED_TITLE_FAILURE: 'Verification expired',
  EMAIL_VERIFIED: 'Your account has been verified.',
  EMAIL_LINK_EXPIRED: 'This link has expired or has already been used.',
}
/** AUTH PAGES TEXT END */

/** MORTGAGE PREIMBURSEMENTS TEXT - START */
export const MP_EXPLAINER_TITLE = 'How bridge loan disbursements work'
export const MP_EXPLAINER_DESCRIPTION =
  'You’ll pay the mortgage on your new home, immediately building equity, and Knock will pre-imburse you for your old mortgage payments for up to 6 months. In addition, if your bridge loan includes home prep funds, we’ll reimburse those as needed.'
export const MP_EXPLAINER_HOW_IT_WORKS_LABEL = 'Here’s exactly how it works:'
export const MP_EXPLAINER_HOW_IT_WORKS_STEPS = [
  '1. Set up a secure connection to your bank account through Dwolla, our third party payments provider',
  '2. We transfer funds to your bank account on a defined cadence based on the disbursement type',
  '3. You continue to pay the mortgage on your old home from the same account, along with any approved home prep',
]
export const MP_TOOLTIP_ACCOUNT_NUMBER =
  'Your bank account number is usually 4 to 17 digits long and can be found as the second set of numbers in the bottom left corner of your checks or by logging into your bank account and searching for the phrase.'
export const MP_TOOLTIP_ROUTING_NUMBER =
  'Your routing number is a nine digit number that can be found in the bottom left corner of your checks or by logging into your bank account and searching for the phrase.'
export const MP_ENROLLMENT_DESCRIPTION =
  'By adding your bank account, we can pre-imburse you for your old mortgage payments and reimburse you for staging, home prep or repairs. To receive these funds, the expenses must be included as part of your approved bridge loan. You are responsible for making these payments directly.'
export const MP_ENROLLMENT_TITLE = 'Set up bridge loan disbursements'

export const mortgagePreimbursementWidget = {
  WIDGET_TITLE: 'Mortgage pre-imbursements',
  WIDGET_DESCRIPTION:
    'A fund transfer for your Mortgage pre-imbursement of your old home’s mortgage will arrive in your bank account.',
  DWOLLA_USE_AUTHORIZATION_LABEL: 'Dwolla use authorization',
  DWOLLA_USE_AUTHORIZATION_INTRO: 'You expressly authorize Knock Lending LLC to collect your personal...',
  dwolla_use_authorization_statement: (
    <>
      You expressly authorize Knock Lending LLC to collect your personal information, including your full name and email
      address and acknowledge that you are wholly responsible for the accuracy and completeness of the data you provide
      to Knock Lending LLC for this purpose. You expressly authorize Knock Lending LLC’s service provider, Dwolla, Inc.
      to originate credit transfers to your financial institution account. In order to originate credit transfers to
      your financial institution account, you will be required to provide certain information which may include your
      financial institution name, financial institution account number, financial institution routing number, and
      similar information (“Financial Information”). Such Financial Information will be collected and processed by our
      service provider, Dwolla, Inc. pursuant to the terms and conditions of their{' '}
      <Anchor href="https://www.dwolla.com/legal/privacy/" target="_blank">
        privacy policy
      </Anchor>
      .
    </>
  ),
  COMPLETED_LABEL: 'Final payment complete',
  completed_description: (paymentValue: string) =>
    `You’ve now received your last payment${
      paymentValue ? ' of ' + paymentValue : ''
    }. You will no longer receive any fund transfers from Knock for the mortgage on your previous house.`,
}

export const MP_NEED_HELP = (
  <Text fz="inherit">
    Need help? Call{' '}
    <Anchor fz="inherit" fw={700} href={`tel:${LISTING_TEAM_SUPPORT_PHONE}`}>
      {formatPhone(LISTING_TEAM_SUPPORT_PHONE)}
    </Anchor>{' '}
    or email{' '}
    <Anchor fz="inherit" fw={700} href={`mailto:${LISTING_TEAM_SUPPORT_EMAIL}`}>
      {LISTING_TEAM_SUPPORT_EMAIL}
    </Anchor>
    .
  </Text>
)

export const MP_SUCCESS_MODAL = {
  TITLE: 'Enrollment Completed',
  MESSAGE: `Your enrollment was successful! You’re now set up for Knock Bridge Loan disbursements.
  
  Reminder: We transfer funds to your bank account on a defined cadence based on the disbursement type. For example, mortgage payments by the first of the month. Home prep, as needed for completed repairs.
  
  You’re required to continue to pay the mortgage on your old home from the same account, along with any approved home prep.`,
}

export const MP_FAILURE_MODAL = {
  TITLE: 'Error, unable to complete enrollment',
  MESSAGE: (
    <>
      We were unable to set up your Knock Bridge Loan disbursements. Please try again.
      <br />
      <br />
      {MP_NEED_HELP}
    </>
  ),
}

/** MORTGAGE PREIMBURSEMENTS TEXT - END */

export const PHOTO_UPLOAD_FORM_URL =
  'https://script.google.com/a/macros/knock.com/s/AKfycbykj8SFNR4LqgcIkpki0m5ZMn2R5FJOtyCvPf4lk_rnv_i1JJzKwHNGCBYtF8HZpfoO/exec'
